import React, { useState } from 'react';
import { z } from 'zod';
import {
  useForm,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from './Form';
import { Input } from './Input';
import { Button } from './Button';
import { useSetPassword } from '../helpers/useUserProfileApi';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import styles from './UserSettingsSetPasswordForm.module.css';

const setPasswordSchema = z
  .object({
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SetPasswordFormValues = z.infer<typeof setPasswordSchema>;

export function UserSettingsSetPasswordForm() {
  const { mutate: setPassword, isPending } = useSetPassword();

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    schema: setPasswordSchema,
  });

  const onSubmit = (values: SetPasswordFormValues) => {
    setPassword(
      {
        newPassword: values.newPassword,
      },
      {
        onSuccess: () => {
          toast.success('Password set successfully');
          form.setValues({
            newPassword: '',
            confirmPassword: '',
          });
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to set password');
        },
      },
    );
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>Reset Password</h3>
      <p className={styles.cardDescription}>
        Since you&apos;re already logged in, you can set a new password directly without needing
        your old one.
      </p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
          <FormItem name="newPassword">
            <FormLabel>New Password</FormLabel>
            <div className={styles.passwordInputWrapper}>
              <FormControl>
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.values.newPassword}
                  onChange={(e) =>
                    form.setValues((prev) => ({ ...prev, newPassword: e.target.value }))
                  }
                />
              </FormControl>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className={styles.toggleButton}
                onClick={() => setShowNewPassword(!showNewPassword)}
                tabIndex={-1}
              >
                {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
            </div>
            <FormDescription>Must be at least 8 characters long.</FormDescription>
            <FormMessage />
          </FormItem>

          <FormItem name="confirmPassword">
            <FormLabel>Confirm New Password</FormLabel>
            <div className={styles.passwordInputWrapper}>
              <FormControl>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.values.confirmPassword}
                  onChange={(e) =>
                    form.setValues((prev) => ({ ...prev, confirmPassword: e.target.value }))
                  }
                />
              </FormControl>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className={styles.toggleButton}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
            </div>
            <FormMessage />
          </FormItem>

          <div className={styles.actions}>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Setting Password...' : 'Set New Password'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
